import { fieldReviewReference } from './field-reference-options';

import type { FieldDocument, FieldOperation, FieldRun, FieldSnapshot } from '@mio/shared';

export const documentKindLabels: Record<string, string> = {
  requirements: 'Requirements',
  policy: 'Policy',
  architecture: 'Architecture',
  runbook: 'Runbook',
  decision: 'Decision record',
  report: 'Report',
  handoff: 'Handoff',
  postmortem: 'Incident review',
  test_plan: 'Test plan',
  scope: 'Project scope',
};
export const documentFormatLabels: Record<FieldDocument['formats'][number], string> = {
  markdown: 'Markdown',
  html: 'HTML',
  json: 'JSON',
  yaml: 'YAML',
  csv: 'CSV',
  pdf: 'PDF',
};
export const runSuiteLabels: Record<FieldRun['suite'], string> = {
  contracts: 'Contract checks',
  security: 'Security checks',
  evaluation: 'Evaluation checks',
  acceptance: 'Acceptance checks',
  rollback: 'Rollback checks',
  demo: 'Demo checks',
};
export const operationKindLabels: Record<FieldOperation['kind'], string> = {
  turn: 'Colleague response',
  document: 'Document preparation',
  review: 'Pull request review',
  analysis: 'Source analysis',
  run: 'Check run',
  assessment: 'Readiness assessment',
};

/** Resolve published evidence only; keep unknown references intact so they remain identifiable. */
export function describeFieldSource(snapshot: FieldSnapshot, id: string): string {
  if (snapshot.discovery?.brief.id === id)
    return `Brief · ${snapshot.discovery.brief.title} · version ${snapshot.discovery.brief.version}`;
  const decision = snapshot.discovery?.decisions.find((item) => item.id === id);
  if (decision) return `Decision · ${decision.title} · version ${decision.version}`;
  const finding = snapshot.discovery?.findings.find((item) => item.id === id);
  if (finding) return `Finding · ${finding.title}`;
  const message = snapshot.messages.find((item) => item.id === id);
  if (message) {
    const actor = snapshot.actors.find((item) => item.id === message.actorId);
    const excerpt = message.text.replace(/\s+/g, ' ').slice(0, 100);
    return `${message.role === 'learner' ? 'You' : (actor?.name ?? message.actorId)} · ${excerpt}${message.text.length > 100 ? '…' : ''}`;
  }
  const ticket = snapshot.tickets.find((item) => item.id === id);
  if (ticket) return `Ticket · ${ticket.title}`;
  const pr = snapshot.prs.find((item) => item.id === id);
  if (pr) return `Pull request · ${pr.title} · ${pr.revision.slice(0, 8)}`;
  const review = fieldReviewReference(snapshot, id);
  if (review) return review.label;
  const document = snapshot.documents.find((item) => item.id === id);
  if (document) return `Document · ${document.title} · version ${document.version}`;
  const run = snapshot.runs.find((item) => item.id === id);
  if (run) return `${runSuiteLabels[run.suite]} · ${run.revision.slice(0, 8)}`;
  const incident = snapshot.incidents.find((item) => item.id === id);
  return incident ? `Incident · ${incident.title}` : id;
}

/** Offer only evidence already present in the learner's published snapshot. */
export function fieldEvidenceOptions(snapshot: FieldSnapshot, excludeId?: string) {
  const ids = new Set([
    ...(snapshot.discovery ? [snapshot.discovery.brief.id] : []),
    ...snapshot.documents.filter((item) => item.status === 'published').map((item) => item.id),
    ...snapshot.prs.map((item) => item.id),
    ...snapshot.runs
      .filter(
        (item) =>
          item.trusted &&
          (item.status === 'passed' || item.status === 'failed') &&
          snapshot.prs.some((pr) => pr.id === item.prId && pr.revision === item.revision),
      )
      .map((item) => item.id),
    ...snapshot.messages.filter((item) => item.role === 'colleague').map((item) => item.id),
    ...snapshot.messages.flatMap((item) => item.sourceIds),
    ...(snapshot.discovery?.decisions.map((item) => item.id) ?? []),
  ]);
  return [...ids]
    .filter((id) => id !== excludeId)
    .map((id) => ({
      id,
      label: describeFieldSource(snapshot, id),
    }));
}
